import { useState, useEffect } from 'react';import { useNavigate } from 'react-router-dom';import { auth, firestore } from '../../firebase';
import {
    onAuthStateChanged,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    updateProfile,
    sendEmailVerification,
    signOut
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp, getDocs, collection, query, where, onSnapshot, updateDoc } from 'firebase/firestore';
import { validateBirthDate, isOver18, createBirthDateString, calculateAge } from './ageVerification';

const useAuth = () => {
    const [user, setUser] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [loading, setLoading] = useState(true);

    // Effect 1: Listen for auth state changes from Firebase Auth
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
            setLoading(true);
            if (currentUser && currentUser.emailVerified) {
                setUser(currentUser);
            } else {
                setUser(null);
                setUserProfile(null);
                setLoading(false);
            }
        });

        return () => unsubscribe();
    }, []);

    // Effect 2: Listen for our user object and fetch/create the Firestore profile
    useEffect(() => {
        if (user) {
            const userRef = doc(firestore, 'users', user.uid);
            const unsubscribeProfile = onSnapshot(userRef, (docSnap) => {
                if (docSnap.exists()) {
                    let userData = docSnap.data();
                    
                    // Check and update age flag if birthDate exists
                    if (userData.birthDate) {
                        const birthDate = new Date(userData.birthDate);
                        const birthDay = birthDate.getDate();
                        const birthMonth = birthDate.getMonth() + 1;
                        const birthYear = birthDate.getFullYear();
                        const currentIsOver18 = isOver18(birthDay, birthMonth, birthYear);
                        
                        // If age flag status has changed, update it
                        if (userData.isOver18 !== currentIsOver18) {
                            updateDoc(userRef, { isOver18: currentIsOver18 });
                            userData = { ...userData, isOver18: currentIsOver18 };
                        }
                    }
                    
                    setUserProfile(userData);
                } else {
                    const newProfile = {
                        email: user.email,
                        createdAt: serverTimestamp(),
                        displayName: user.displayName,
                        name: user.displayName,
                        avatar: 'https://firebasestorage.googleapis.com/v0/b/bonfire-d8db1.firebasestorage.app/o/Profile_Pictures%2Flogo.png?alt=media&token=15ac7dfc-d970-49f2-a9c6-429dd0656f0a',
                        bio: 'Welcome to Bonfire!',
                        bgColor: '#ffd9ba',
                        usernameColor: '#c84848',
                        stylePreference: 'light'
                    };
                    setDoc(userRef, newProfile);
                    setUserProfile(newProfile);
                }
                setLoading(false);
            }, (error) => {
                console.error("Error fetching user profile:", error);
                setUserProfile(null);
                setLoading(false);
            });

            return () => unsubscribeProfile();
        }
    }, [user]); // This effect now correctly depends on the user state

    useEffect(() => {
        if (userProfile?.stylePreference === 'dark') {
            document.body.classList.add('dark');
        } else {
            document.body.classList.remove('dark');
        }
    }, [userProfile]);

    return { user, userProfile, loading };
};


const useAuthentication = () => {
    const navigate = useNavigate();
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [verificationSent, setVerificationSent] = useState(false);

    const getFriendlyErrorMessage = (errorCode) => {
        switch (errorCode) {
            case 'auth/invalid-email':
                return 'Please enter a valid email address.';
            case 'auth/user-not-found':
            case 'auth/wrong-password':
                return 'Invalid email or password. Please try again.';
            case 'auth/email-already-in-use':
                return 'An account with this email already exists.';
            case 'auth/password-does-not-meet-requirements':
                return 'Password should contain 8-36 characters, a lower and uppercase character, a number, and a special character.';
            case 'auth/email-not-verified':
                return 'Please verify your email before logging in. A new verification email has been sent.';
            default:
                return 'An unexpected error occurred. Please try again.';
        }
    };

    const signUp = async (email, password, username, birthDate) => {
        setLoading(true);
        setError('');
        setVerificationSent(false);
        try {
            // Parse birth date
            const birthDateObj = new Date(birthDate);
            const birthDay = birthDateObj.getDate();
            const birthMonth = birthDateObj.getMonth() + 1; // Months are 0-indexed
            const birthYear = birthDateObj.getFullYear();

            // Validate birth date
            const birthDateValidation = validateBirthDate(birthDay, birthMonth, birthYear);
            if (!birthDateValidation.isValid) {
                setError(birthDateValidation.error);
                setLoading(false);
                return;
            }

            const usersCollectionRef = collection(firestore, 'users');
            const usernameQuery = query(usersCollectionRef, where("name", "==", username));
            const usernameQuerySnapshot = await getDocs(usernameQuery);

            if (!usernameQuerySnapshot.empty) {
                setError('This username is already taken. Please choose another one.');
                setLoading(false);
                return;
            }

            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            if (userCredential && userCredential.user) {
                const user = userCredential.user;
                await updateProfile(user, { displayName: username });
                
                // Calculate age and get isOver18 flag
                const over18 = isOver18(birthDay, birthMonth, birthYear);
                const birthDateString = createBirthDateString(birthDay, birthMonth, birthYear);
                
                // Store age verification data in user document
                const userRef = doc(firestore, 'users', user.uid);
                const initialProfile = {
                    email: user.email,
                    createdAt: serverTimestamp(),
                    displayName: username,
                    name: username,
                    birthDate: birthDateString,
                    isOver18: over18,
                    avatar: 'https://firebasestorage.googleapis.com/v0/b/bonfire-d8db1.firebasestorage.app/o/Profile_Pictures%2Flogo.png?alt=media&token=15ac7dfc-d970-49f2-a9c6-429dd0656f0a',
                    bio: 'Welcome to Bonfire!',
                    bgColor: '#ffd9ba',
                    usernameColor: '#c84848',
                    stylePreference: 'light'
                };
                await setDoc(userRef, initialProfile);
                
                await sendEmailVerification(user);
                await signOut(auth);

                setVerificationSent(true);
                setError('A verification email has been sent. Please check your inbox and verify your account before logging in.');
            }
        } catch (error) {
            setError(getFriendlyErrorMessage(error.code));
        }
        setLoading(false);
    };

    const signIn = async (email, password) => {
        setLoading(true);
        setError('');
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            if (!userCredential.user.emailVerified) {
                await sendEmailVerification(userCredential.user);
                await signOut(auth);
                setError(getFriendlyErrorMessage('auth/email-not-verified'));
            } else {
                // Successfully logged in and email verified — navigate to home
                navigate('/friends');
            }
        } catch (error) {
            if (error.code === 'auth/wrong-password' || error.code === 'auth/user-not-found') {
                 setError('Invalid email or password. Please try again.');
            } else {
                 setError(getFriendlyErrorMessage(error.code));
            }
        }
        setLoading(false);
    };

    return { signUp, signIn, error, loading, verificationSent };
};

export { useAuth, useAuthentication };